import { getBaseUrl } from './constants';

export interface CanonicalOptions {
  /** Current path including locale prefix, e.g. `/us-en/about` */
  path: string;
  /** Override base URL (defaults to brand base URL) */
  baseUrl?: string;
  /** CMS-provided canonical path (relative to locale, e.g. `about/history`) */
  cmsCanonical?: string | null;
  /** Current locale slug, e.g. `us-en` — required when cmsCanonical is relative */
  locale?: string;
}

/**
 * Query parameters that are preserved in canonical URLs.
 * All others (filters, search) are stripped per spec §5.
 */
const CANONICAL_ALLOWED_PARAMS = new Set(['page']);

/**
 * Ensure a pathname ends with a trailing slash.
 * File-like paths (containing a dot in the last segment) are left unchanged.
 */
function ensureTrailingSlash(pathname: string): string {
  const lastSegment = pathname.split('/').pop() || '';
  if (lastSegment.includes('.')) return pathname;
  return pathname.endsWith('/') ? pathname : `${pathname}/`;
}

/**
 * Filter search params to only keep canonical-safe parameters.
 * - Strips all filter/search params
 * - Keeps `page` only when value is a valid integer > 1
 */
function filterCanonicalParams(searchParams: URLSearchParams): string {
  const filtered = new URLSearchParams();

  for (const key of CANONICAL_ALLOWED_PARAMS) {
    const value = searchParams.get(key);
    if (value == null) continue;

    if (key === 'page') {
      const pageNum = Number.parseInt(value, 10);
      if (Number.isNaN(pageNum) || pageNum <= 1) continue;
      filtered.set(key, String(pageNum));
    } else {
      filtered.set(key, value);
    }
  }

  const qs = filtered.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Build an absolute canonical URL per spec §5.
 *
 * Rules:
 * - Always absolute (https://domain/...)
 * - Always has trailing slash (before query string)
 * - Strips all query params except `page`
 * - Drops `page=1` (canonical points to unpaginated URL)
 * - CMS canonical overrides self-referencing
 */
export function buildCanonicalUrl({
  path,
  baseUrl,
  cmsCanonical,
  locale,
}: CanonicalOptions): string {
  const base = baseUrl || getBaseUrl();

  if (cmsCanonical) {
    const isAbsolute = cmsCanonical.startsWith('http');
    if (isAbsolute) {
      return ensureTrailingSlash(cmsCanonical);
    }
    const prefix = locale ? `/${locale}` : '';
    const normalizedCanonical = cmsCanonical.startsWith('/')
      ? cmsCanonical
      : `${prefix}/${cmsCanonical}`;
    return `${base}${ensureTrailingSlash(normalizedCanonical)}`;
  }

  const url = new URL(path, base);
  const search = filterCanonicalParams(url.searchParams);
  const pathname = ensureTrailingSlash(url.pathname);

  return `${base}${pathname}${search}`;
}

/**
 * Build a canonical path (without base URL) for Next.js metadata.
 * Next.js resolves relative alternates against metadataBase.
 */
export function buildCanonicalPath(path: string): string {
  const url = new URL(path, 'https://placeholder.com');
  const search = filterCanonicalParams(url.searchParams);
  const pathname = ensureTrailingSlash(url.pathname);
  return `${pathname}${search}`;
}
